// Generated by TaroBase SDK Generator: 2026-08-23T03:59:35.512Z
// Collection: notifications/$recipientAddress/items/$notificationId
// Edits may be overwritten by subsequent runs!

import {
  get, getMany, set, subscribe, getFiles, setFile, runQuery, count, aggregate, transformValues,
  Time, Token, Increment, Address, PublicKey,
  TimeOperation, IncrementOperation, TokenAmount, AddressType,
  TarobaseMetadata, DocumentOperation, FileItem, setMany, formatError,
} from '../db-client';
import type { AggregateResult, AggregateOperation } from '../db-client';

/**
 * In-app notifications for users. Each user has their own notifications subcollection. Notifications are written by the backend (PROJECT_VAULT_ADDRESS) — currently as part of the wallpaper approval fan-out, where for each subscriber of the creator a notification record is created when a wallpaper transitions to 'published'. Fields are cached at write time (fromUsername, wallpaperTitle, wallpaperThumbnailUrl) to avoid extra lookups on render. The 'type' field is open-ended ('newWallpaper' initially) to allow new notification types in the future without schema changes. Recipients can read their own notifications, flip 'read' from false to true (mark-as-read), and delete their notifications.
 */

export interface NotificationsItemsRequest {
  recipient: AddressType;
  type: string;
  fromUser: AddressType;
  fromUsername?: string;
  wallpaperId?: string;
  wallpaperTitle?: string;
  wallpaperThumbnailUrl?: string;
  createdAt: number | TimeOperation | IncrementOperation | TokenAmount;
  read: boolean;
}

export interface NotificationsItemsResponse {
  recipient: string;
  type: string;
  fromUser: string;
  fromUsername?: string;
  wallpaperId?: string;
  wallpaperTitle?: string;
  wallpaperThumbnailUrl?: string;
  createdAt: number;
  read: boolean;
  id: string;
  tarobase_created_at: number;
}

/**
 * Build a NotificationsItems operation for use with setMany.
 * @returns A DocumentOperation that can be passed to setMany.
 */
export function buildNotificationsItems(recipientAddress: string, notificationId: string, data: NotificationsItemsRequest): DocumentOperation {
  const doc = transformValues(data);
  return { path: `notifications/${recipientAddress}/items/${notificationId}`, document: doc };
}

/**
 * Backend only (PROJECT_VAULT_ADDRESS). The backend signs as PROJECT_VAULT and writes one notification row per subscriber when a creator's wallpaper transitions to 'published'. @newData.recipient must equal the $recipientAddress path segment. Fields recipient, type, fromUser, and createdAt are immutable after create (! modifier). Optional fields (fromUsername, wallpaperId, wallpaperTitle, wallpaperThumbnailUrl) can be null. 'read' defaults to false on create. No bootstrap script needed — this is a per-user dynamic collection that is populated organically as events occur. (Create/Update Single Item)
 * @returns A boolean indicating whether the operation succeeded (true) or failed (false). Always check this value to confirm the write worked.
 */
export async function setNotificationsItems(recipientAddress: string, notificationId: string, data: NotificationsItemsRequest): Promise<boolean> {
  try {
    const transformedData = transformValues(data);
    const succeeded = await set(`notifications/${recipientAddress}/items/${notificationId}`, transformedData);
    return !!succeeded;
  } catch (error) {
    console.error(`Error setting NotificationsItems: ${formatError(error)}`);
    return false;
  }
}

export type NotificationsItemsRequestUpdate = Partial<NotificationsItemsRequest>;

/**
 * Build a NotificationsItems update operation for use with setMany.
 * @returns A DocumentOperation that can be passed to setMany.
 */
export function buildUpdateNotificationsItems(recipientAddress: string, notificationId: string, data: NotificationsItemsRequestUpdate): DocumentOperation {
  const doc = transformValues(data);
  return { path: `notifications/${recipientAddress}/items/${notificationId}`, document: doc };
}

/**
 * Recipient only (caller equals $recipientAddress) and ONLY to mark as read. The update rule pins recipient/type/fromUser/wallpaperId/createdAt to their existing values and requires @newData.read == true, so the only allowed mutation is flipping 'read' from false to true. All other fields are immutable post-create. (Update Single Item)
 * @returns A boolean indicating whether the operation succeeded (true) or failed (false). Always check this value to confirm the update worked.
 */
export async function updateNotificationsItems(recipientAddress: string, notificationId: string, data: NotificationsItemsRequestUpdate): Promise<boolean> {
  try {
    const transformedData = transformValues(data);
    const succeeded = await set(`notifications/${recipientAddress}/items/${notificationId}`, transformedData);
    return !!succeeded;
  } catch (error) {
    console.error(`Error updating NotificationsItems: ${formatError(error)}`);
    return false;
  }
}

/**
 * 
  Read Operation Details: Recipient only. Caller must equal $recipientAddress path segment. Other users CANNOT read another user's notifications. Returns all fields including cached display data (fromUsername, wallpaperTitle, wallpaperThumbnailUrl) and the read flag.
   (Get Single Item)
 */
export async function getNotificationsItems(recipientAddress: string, notificationId: string): Promise<NotificationsItemsResponse | null> {
  try {
    const result = await get(`notifications/${recipientAddress}/items/${notificationId}`);
    return result as NotificationsItemsResponse | null;
  } catch (error) {
    console.error(`Error getting NotificationsItems: ${formatError(error)}`);
    return null;
  }
}

/**
 * Subscribes to changes in a single NotificationsItems document. (
  Read Operation Details: Recipient only. Caller must equal $recipientAddress path segment. Other users CANNOT read another user's notifications. Returns all fields including cached display data (fromUsername, wallpaperTitle, wallpaperThumbnailUrl) and the read flag.
  )
 */
export function subscribeNotificationsItems(callback: (data: NotificationsItemsResponse | null) => void, recipientAddress: string, notificationId: string): Promise<() => Promise<void>> {
  return subscribe(`notifications/${recipientAddress}/items/${notificationId}`, { // Use the item's basePath
    onData: (data) => {
      callback(data as NotificationsItemsResponse | null);
    },
    onError: (error) => {
      console.error(`Error subscribing to NotificationsItems at path '${`notifications/${recipientAddress}/items/${notificationId}`}': ${formatError(error)}`);
    }
  });
}

/**
 * Get many NotificationsItems items from collection notifications/${recipientAddress}/items
 
  Read Operation Details: Recipient only. Caller must equal $recipientAddress path segment. Other users CANNOT read another user's notifications. Returns all fields including cached display data (fromUsername, wallpaperTitle, wallpaperThumbnailUrl) and the read flag.
  
 */
export async function getManyNotificationsItems(recipientAddress: string, filter: string = ""): Promise<NotificationsItemsResponse[]> {
  try {
    const results = await get(`notifications/${recipientAddress}/items`, { prompt: filter });
    return Array.isArray(results) ? results as NotificationsItemsResponse[] : [];
  } catch (error) {
    console.error(`Error getting NotificationsItems collection at path 'notifications/${recipientAddress}/items': ${formatError(error)}`);
    return [];
  }
}

/**
 * Subscribe to changes in NotificationsItems collection at notifications/${recipientAddress}/items
 
  Read Operation Details: Recipient only. Caller must equal $recipientAddress path segment. Other users CANNOT read another user's notifications. Returns all fields including cached display data (fromUsername, wallpaperTitle, wallpaperThumbnailUrl) and the read flag.
  
 */
export function subscribeManyNotificationsItems(callback: (data: NotificationsItemsResponse[]) => void, recipientAddress: string, filter: string = ""): Promise<() => Promise<void>> {
  return subscribe(`notifications/${recipientAddress}/items`, {
    prompt: filter,
    onData: (data) => {
      callback(Array.isArray(data) ? data as NotificationsItemsResponse[] : []);
    },
    onError: (error) => {
       console.error(`Error subscribing to NotificationsItems collection at path '${`notifications/${recipientAddress}/items`}': ${formatError(error)}`);
    }
  });
}

/**
 * Get all NotificationsItems items from collection notifications/${recipientAddress}/items
 
  Read Operation Details: Recipient only. Caller must equal $recipientAddress path segment. Other users CANNOT read another user's notifications. Returns all fields including cached display data (fromUsername, wallpaperTitle, wallpaperThumbnailUrl) and the read flag.
  
 */
export async function getAllNotificationsItems(recipientAddress: string, filter: string = ""): Promise<NotificationsItemsResponse[]> {
  try {
    const results = await get(`notifications/*/items`, { prompt: filter });
    return Array.isArray(results) ? results as NotificationsItemsResponse[] : [];
  } catch (error) {
    console.error(`Error getting NotificationsItems collection at path 'notifications/${recipientAddress}/items': ${formatError(error)}`);
    return [];
  }
}

/**
 * Subscribe to changes in NotificationsItems collection at notifications/${recipientAddress}/items
 
  Read Operation Details: Recipient only. Caller must equal $recipientAddress path segment. Other users CANNOT read another user's notifications. Returns all fields including cached display data (fromUsername, wallpaperTitle, wallpaperThumbnailUrl) and the read flag.
  
 */
export function subscribeAllNotificationsItems(callback: (data: NotificationsItemsResponse[]) => void, recipientAddress: string, filter: string = ""): Promise<() => Promise<void>> {
  return subscribe(`notifications/*/items`, {
    prompt: filter,
    onData: (data) => {
      callback(Array.isArray(data) ? data as NotificationsItemsResponse[] : []);
    },
    onError: (error) => {
       console.error(`Error subscribing to NotificationsItems collection at path '${`notifications/*/items`}': ${formatError(error)}`);
    }
  });
}

/**
 * 
  Delete Operation Details: Two allowed paths: (1) RECIPIENT — caller equals $recipientAddress can dismiss/delete their own notifications. (2) BACKEND CLEANUP — PROJECT_VAULT_ADDRESS can delete notifications for cleanup (e.g., when a wallpaper is hard-deleted or a creator profile is torn down, the backend may remove stale notification rows referencing them).
   (Delete Single Item)
 * @returns A boolean indicating whether the operation succeeded (true) or failed (false). Always check this value to confirm the delete worked.
 */
export async function deleteNotificationsItems(recipientAddress: string, notificationId: string): Promise<boolean> {
  try {
    const succeeded = await set(`notifications/${recipientAddress}/items/${notificationId}`, null);
    return !!succeeded;
  } catch (error) {
    console.error(`Error deleting NotificationsItems: ${formatError(error)}`);
    return false;
  }
}

/**
 * Build a delete operation for NotificationsItems for use with setMany.
 * @returns A DocumentOperation that can be passed to setMany.
 */
export function buildDeleteNotificationsItems(recipientAddress: string, notificationId: string): DocumentOperation {
  return { path: `notifications/${recipientAddress}/items/${notificationId}`, document: null };
}

